import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { Link } from 'react-router-dom';

const PricingWrapper = styled.div`
  padding: ${theme.spacing['4xl']} 0;
  min-height: 80vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.text.secondary};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const CurrentStatusCard = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['3xl']};
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};
`;

const StatusTitle = styled.h2`
  font-size: ${theme.fontSizes['3xl']};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
`;

const StatusDescription = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.primary};
  max-width: 600px;
  margin: 0 auto ${theme.spacing.xl};
  line-height: 1.6;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.xl};
  margin-top: ${theme.spacing['3xl']};
  
  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PricingCard = styled.div<{ featured?: boolean }>`
  background-color: ${props => props.featured ? theme.colors.primary : theme.colors.background};
  border: 2px solid ${props => props.featured ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing['2xl']};
  text-align: center;
  position: relative;
  transition: transform ${theme.transitions.base};
  
  ${props => props.featured && `
    transform: scale(1.05);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  `}
  
  &:hover {
    transform: translateY(-4px) ${props => props.featured ? 'scale(1.05)' : ''};
  }
`;

const PlanBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${theme.colors.secondary};
  color: white;
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.radii.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const PlanIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${theme.spacing.md};
`;

const PlanName = styled.h3<{ featured?: boolean }>`
  font-size: ${theme.fontSizes['2xl']};
  margin-bottom: ${theme.spacing.md};
  color: ${props => props.featured ? 'white' : theme.colors.text.primary};
`;

const PlanPrice = styled.div<{ featured?: boolean }>`
  font-size: ${theme.fontSizes['3xl']};
  font-weight: bold;
  margin-bottom: ${theme.spacing.sm};
  color: ${props => props.featured ? 'white' : theme.colors.primary};
`;

const PlanPeriod = styled.span<{ featured?: boolean }>`
  font-size: ${theme.fontSizes.base};
  color: ${props => props.featured ? 'rgba(255, 255, 255, 0.9)' : theme.colors.text.secondary};
`;

const PlanDescription = styled.p<{ featured?: boolean }>`
  color: ${props => props.featured ? 'rgba(255, 255, 255, 0.9)' : theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.xl};
  line-height: 1.6;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: ${theme.spacing.xl};
  text-align: left;
`;

const Feature = styled.li<{ featured?: boolean }>`
  padding: ${theme.spacing.sm} 0;
  color: ${props => props.featured ? 'rgba(255, 255, 255, 0.95)' : theme.colors.text.secondary};
  display: flex;
  align-items: center;
  
  &:before {
    content: '✓';
    color: ${props => props.featured ? 'white' : theme.colors.success};
    font-weight: bold;
    margin-right: ${theme.spacing.sm};
  }
`;

const ComingSoonSection = styled.div`
  background-color: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['2xl']};
  margin-top: ${theme.spacing['3xl']};
  text-align: center;
`;

const ComingSoonTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const ComingSoonDescription = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

export const PricingPage: React.FC = () => {
  return (
    <PricingWrapper>
      <Container>
        <Header>
          <Title>Simple, Transparent Pricing</Title>
          <Subtitle>
            At the moment, Promptasaurus is free while we work towards our Minimum Lovable Product. 
            Expect that there will always be a free tier and toolset, but one day soon we will have paid plans.
          </Subtitle>
        </Header>

        <CurrentStatusCard>
          <StatusTitle>🎉 Currently Free for Everyone!</StatusTitle>
          <StatusDescription>
            We're in active development and want you to experience the full power of Promptasaurus. 
            All features are currently available at no cost. Help us improve by providing feedback!
          </StatusDescription>
          <Button as={Link} to="/get-started" size="large">
            Get Started for Free
          </Button>
        </CurrentStatusCard>

        <PricingGrid>
          <PricingCard>
            <PlanIcon>🦕</PlanIcon>
            <PlanName>Free</PlanName>
            <PlanPrice>$0<PlanPeriod>/month</PlanPeriod></PlanPrice>
            <PlanDescription>
              Perfect for individuals getting started with AI prompt enhancement
            </PlanDescription>
            <FeatureList>
              <Feature>Basic prompt improvement</Feature>
              <Feature>Access to Fossil Fills</Feature>
              <Feature>5 templates per month</Feature>
              <Feature>Community support</Feature>
              <Feature>Browser extension</Feature>
            </FeatureList>
            <Button variant="secondary" fullWidth>
              Always Available
            </Button>
          </PricingCard>

          <PricingCard featured>
            <PlanBadge>COMING SOON</PlanBadge>
            <PlanIcon style={{ fontSize: '2.5rem' }}>🦖</PlanIcon>
            <PlanName featured>Raptor</PlanName>
            <PlanPrice featured>$9<PlanPeriod featured>/month</PlanPeriod></PlanPrice>
            <PlanDescription featured>
              For power users and professionals who need advanced features
            </PlanDescription>
            <FeatureList>
              <Feature featured>Everything in Free</Feature>
              <Feature featured>Unlimited improvements</Feature>
              <Feature featured>Custom templates</Feature>
              <Feature featured>Advanced AI models</Feature>
              <Feature featured>Priority support</Feature>
              <Feature featured>API access</Feature>
            </FeatureList>
            <Button variant="secondary" fullWidth style={{ backgroundColor: 'white', color: theme.colors.primary }}>
              Coming Soon
            </Button>
          </PricingCard>

          <PricingCard>
            <PlanIcon>🦖</PlanIcon>
            <PlanName>Rex</PlanName>
            <PlanPrice>$29<PlanPeriod>/month</PlanPeriod></PlanPrice>
            <PlanDescription>
              For teams and organizations that need collaboration features
            </PlanDescription>
            <FeatureList>
              <Feature>Everything in Raptor</Feature>
              <Feature>5 team members</Feature>
              <Feature>Shared templates</Feature>
              <Feature>Team analytics</Feature>
              <Feature>Admin controls</Feature>
              <Feature>Dedicated support</Feature>
            </FeatureList>
            <Button variant="secondary" fullWidth>
              Coming Soon
            </Button>
          </PricingCard>
        </PricingGrid>

        <ComingSoonSection>
          <ComingSoonTitle>🚀 What's Coming Next?</ComingSoonTitle>
          <ComingSoonDescription>
            We're working hard to bring you premium features including advanced AI models, 
            custom template builders, team collaboration tools, and API access. 
            Join us now and lock in early adopter benefits when we launch paid plans!
          </ComingSoonDescription>
        </ComingSoonSection>
      </Container>
    </PricingWrapper>
  );
};